// CallInvitationBanner — CALL-INVITATION (tickets/fable.md, Nika
// 2026-08-13: "add notification/beautiful banner when its call invitation
// on given scdheduled time or calendar meeting"). A beautiful, app-wide,
// actionable banner that appears when a scheduled meeting reaches its
// scheduled moment — not a passive card buried on one page.
//
// DATA: functions/notifications/meetingInviteBanner.js's
// `activeMeetingInvite(root)` — a pure view over the durable
// `meeting_starting` notifications the SERVER writes (see
// server/src/domains/notifications/services/meetingStartingScheduler.js
// for the time trigger + server/.../meetingStartingNotifier.js for the
// event -> room design decision). No client-side fetch/poll needed for the
// "appear" moment: subscribeNotifications.js's existing realtime socket
// push already lands new rows straight into `root.notifs`, which this
// component subscribes to directly.
//
// JOIN: reuses the calendar package's OWN join dispatch verbatim
// (functions/eventMeetJoin.js joinEventCall, via the
// functions/notifications/meetingInviteBanner.js joinMeetingInvite
// wrapper) — find-or-create a meet_rooms row keyed by calendarEventId,
// then hand off activeRoomId/meetView + connectCall. No window.location,
// no el.router hard-navigation needed either: connectCall works from any
// route, and MeetCallWidget (already mounted at the shell root) surfaces
// the live call as a floating PiP the instant the user isn't on /meet.
//
// DISMISS: markNotificationRead on the underlying row (durable, no
// separate pref/cookie) — "stays dismissed" for good, cross-device, with
// exactly one dismissal mechanism to keep in sync.
//
// CHROME: the elevated-card family CreditBlocker.js / AiActionConfirmCard.js
// already established (`background:'document'`, `round:'radiusSheet'`,
// `shadow:'overlay'` — DESIGN_LANGUAGE.md §2.3's two-shadow budget, never a
// bespoke boxShadow string) — "should feel related" per the ticket. Unlike
// CreditBlocker this is NOT a full-viewport blocker (a meeting starting
// should never stop you from working): no scrim, just the floating card
// itself, bottom-center above the AI composer — the GlobalNotification
// toast family's own anchor (bottom C1, centered), one consistent home
// for transient shell messages (2026-09-01 per Nika; it was fixed
// top-right before). Other corners stay claimed as they were
// (bottom-left: CreditBanner/DemoClaimBanner/PersonaPill; bottom-right:
// MeetCallWidget's own PiP dock).
//
// ZERO-SHIFT (DESIGN_LANGUAGE.md §8.4): position:fixed overlay, never
// inserted in-flow — the shell-root wrapper is `display:'contents'` so it
// never becomes a static grid child (the DemoClaimBanner/CreditBlocker
// lesson: a real box here pushes routed content into a collapsed implicit
// grid row). `hide:` (not `if:`) keeps the Card mounted in the DOM at all
// times so it can be found/announced the instant it should appear, rather
// than being freshly created.
//
// TIME-TRIGGERING ON THE CLIENT: appearing needs no client timer (the
// server sweep + realtime push does that). Self-expiring while ALREADY
// showing (an event's endAt quietly passing with no other app activity in
// between) does — `_meetingInviteTick`, nudged every 30s from the shell's
// onCreate (app.js), visibility-gated so a backgrounded tab does no
// needless work (browsers throttle hidden-tab timers heavily regardless;
// this is a deliberate skip, not a hope). See app.js's own comment beside
// `el._meetingInviteTickTimer`.

export const CallInvitationBanner = {
  tag: 'div',
  display: 'contents',
  subscribeTo: ['notifs', 'rooms', 'activeRoomId', 'signedIn', 'workspace', '_meetingInviteTick'],

  Card: {
    hide: (el, s) => !el.call('activeMeetingInvite', s.root),
    // Bottom-center, ABOVE the AI composer — the GlobalNotification toast
    // family's exact anchor (bottom C1, centered), so transient shell
    // messages share ONE consistent home (Nika 2026-09-01 "make
    // CallInvitationBanner above ai textarea so we make it consistent").
    // Was fixed top-right; that corner rationale is retired with it.
    position: 'fixed',
    bottom: 'C1',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: '1600',
    background: 'document',
    round: 'radiusSheet',
    shadow: 'overlay',
    flow: 'y',
    gap: 'Z1',
    padding: 'B',
    width: 'H1',
    maxWidth: 'calc(100dvw - var(--spacing-B))',
    boxSizing: 'border-box',
    role: 'status',
    ariaLive: 'polite',
    'aria-label': (el) => el.call('polyglot', 'workspace.callInvite.aria'),
    '@mobileL': {
      left: 'Z',
      right: 'Z',
      transform: 'none',
      width: 'auto',
      maxWidth: 'none'
    },

    Header: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z1',

      Lead: {
        flow: 'x',
        align: 'center',
        gap: 'Y1',
        minWidth: '0',

        Icon: { name: 'video', boxSize: 'A', color: 'caption' },
        Eyebrow: {
          tag: 'span',
          fontSize: 'Y',
          fontWeight: '500',
          textTransform: 'uppercase',
          letterSpacing: '.07em',
          color: 'caption',
          text: '{{ workspace.callInvite.eyebrow | polyglot }}'
        }
      },

      // THE house close X (brand CloseButton — F2, one X for every panel);
      // same control as CreditBanner.Dismiss.
      Dismiss: {
        extends: 'CloseButton',
        title: (el) => el.call('polyglot', 'workspace.callInvite.dismiss'),
        'aria-label': (el) => el.call('polyglot', 'workspace.callInvite.dismissAria'),
        onClick: (e, el, s) => {
          e.stopPropagation()
          const notif = el.call('activeMeetingInvite', s.root)
          if (notif) el.call('markNotificationRead', notif.id)
        }
      }
    },

    Title: {
      tag: 'p',
      margin: '0',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      lineHeight: '1.3',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        const notif = el.call('activeMeetingInvite', s.root)
        const meta = notif && notif.metadata
        return (
          (meta && meta.eventTitle) ||
          (notif && notif.title) ||
          el.call('polyglot', 'workspace.callInvite.untitled')
        )
      }
    },

    // Coarse, honest lead-time label — re-evaluated on every
    // `_meetingInviteTick` (never a live per-second countdown, which would
    // be a fake-precision affordance nothing here can actually promise).
    // The Card has a fixed token width (not shrink-to-fit), so this text
    // changing length over time never shifts any sibling or the card's own
    // box — no separate minWidth floor needed for DESIGN_LANGUAGE §8.4.
    Meta: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) => {
        const notif = el.call('activeMeetingInvite', s.root)
        const startAt = notif && notif.metadata && notif.metadata.startAt
        if (!startAt) return el.call('polyglot', 'workspace.callInvite.startingNow')
        const diffMs = new Date(startAt).getTime() - Date.now()
        if (!Number.isFinite(diffMs) || diffMs <= 60000)
          return el.call('polyglot', 'workspace.callInvite.startingNow')
        const mins = Math.round(diffMs / 60000)
        return el.call('polyglot', 'workspace.callInvite.startsIn').replace('{count}', mins)
      }
    },

    Actions: {
      flow: 'x',
      gap: 'Z1',
      marginTop: 'V',

      // The one place a strong affordance is justified (DESIGN_LANGUAGE.md
      // §4.4: accent hue is for links/badges, NEVER a primary button fill —
      // "ink does"). Same house primary as CreditBlocker.Cta.Button: a
      // solid ink capsule (`title` / `title-reversed`), not a hue fill.
      // Hover uses `filter:brightness()` per §4.3's own prescription for
      // filled/ink surfaces (a `background:'hover'` wash doesn't read on
      // top of an already-opaque fill) rather than dimming with `opacity`
      // (§8.1/§8.5: "never dim with opacity — the composite silently
      // halves the contrast ratio").
      Join: {
        tag: 'button',
        type: 'button',
        flex: '1',
        minHeight: 'controlH',
        padding: '0 B',
        fontWeight: '700',
        fontSize: 'Z2',
        round: 'radiusPill',
        background: 'title',
        color: 'title-reversed',
        border: 'none',
        cursor: 'pointer',
        transition: '150ms defaultBezier filter',
        ':hover': { filter: 'brightness(1.1)' },
        text: '{{ workspace.callInvite.join | polyglot }}',
        onClick: async (e, el, s) => {
          e.stopPropagation()
          const notif = el.call('activeMeetingInvite', s.root)
          if (notif) await el.call('joinMeetingInvite', notif)
        }
      }
    }
  }
}
